import type { DomainGroup, Maturity, Platform } from './taxonomy.js';
import type { PrivacyClass } from './privacy.js';

export type SearchResultKind =
  | 'system'
  | 'task'
  | 'capture'
  | 'note'
  | 'source'
  | 'review';

export const SEARCH_RESULT_KINDS: readonly SearchResultKind[] = [
  'system', 'task', 'capture', 'note', 'source', 'review',
] as const;

export interface SearchFilters {
  readonly group?: DomainGroup;
  readonly maturity?: Maturity;
  readonly platform?: Platform;
  readonly privacyClass?: PrivacyClass;
  readonly kind?: SearchResultKind;
}

export interface SearchQuery {
  readonly text: string;
  readonly filters?: SearchFilters;
  readonly limit?: number;
}

export interface SearchResult {
  readonly id: string;
  readonly kind: SearchResultKind;
  readonly title: string;
  /** Redacted preview honoring the result's privacy class. */
  readonly preview: string;
  readonly privacyClass: PrivacyClass;
  /** Where to navigate to open this result. */
  readonly surface: string;
}

export interface SearchResultGroup {
  readonly kind: SearchResultKind;
  readonly results: readonly SearchResult[];
}

export interface CommandContext {
  readonly currentSurface: string;
}

export interface CommandDescriptor {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
}

export interface CommandSearchService {
  search(query: SearchQuery): Promise<readonly SearchResultGroup[]>;
  commands(context: CommandContext): readonly CommandDescriptor[];
}

import { PRIVACY_CLASSES, isPrivacyClass } from './privacy.js';
import { DOMAIN_GROUPS, MATURITIES, PLATFORMS } from './taxonomy.js';

/**
 * Parse a raw search string into free text + typed filter tokens. Pure.
 *
 * Recognised tokens: `group:`, `maturity:`, `platform:`, `privacy:`, `kind:`.
 * Unknown tokens (or tokens with invalid values) fall back to free text, so the
 * box is forgiving — a stray colon never eats the query.
 */
export function parseSearchQuery(raw: string): SearchQuery {
  const filters: {
    group?: DomainGroup;
    maturity?: Maturity;
    platform?: Platform;
    privacyClass?: PrivacyClass;
    kind?: SearchResultKind;
  } = {};
  const text: string[] = [];

  for (const tok of raw.split(/\s+/).filter((t) => t.length > 0)) {
    const m = /^([a-z]+):(.+)$/i.exec(tok);
    if (!m) {
      text.push(tok);
      continue;
    }
    const key = m[1]!.toLowerCase();
    const val = m[2]!;
    if (key === 'privacy' && isPrivacyClass(val)) filters.privacyClass = val;
    else if (key === 'kind' && (SEARCH_RESULT_KINDS as readonly string[]).includes(val)) filters.kind = val as SearchResultKind;
    else if (key === 'group' && (DOMAIN_GROUPS as readonly string[]).includes(val)) filters.group = val as DomainGroup;
    else if (key === 'maturity' && (MATURITIES as readonly string[]).includes(val)) filters.maturity = val as Maturity;
    else if (key === 'platform' && (PLATFORMS as readonly string[]).includes(val)) filters.platform = val as Platform;
    else text.push(tok); // unknown key or invalid value → treat as text
  }

  return { text: text.join(' '), filters };
}

/** Known privacy filter values, for building filter UIs. */
export const SEARCH_PRIVACY_VALUES = PRIVACY_CLASSES;
